import { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useLocation, matchPath } from 'react-router-dom'
import { PageLoader } from '../components/common/Loading'
import AdminLayout from '../layouts/admin/AdminLayout'
import AuthLayout from '../layouts/auth/AuthLayout'
import DoctorLayout from '../layouts/doctor/DoctorLayout'
import PatientLayout from '../layouts/patient/PatientLayout'
import PublicLayout from '../layouts/public/PublicLayout'
import ProtectedRoute from './ProtectedRoute'
import PortfolioPage from '../features/public-portfolio/PortfolioPage'
import DoctorDetailsPage from '../features/public-portfolio/DoctorDetailsPage'
import RegisterPage from '../features/auth/RegisterPage'
import {
  PaymentsPage,
  DemoPaymentPage,
  ReceiptPage,
} from '../features/payments/PaymentPages'
import RoleRoute from './RoleRoute'
import {
  DashboardPage,
  ProfilePage,
  PasswordPage,
  NotificationsPage,
  FilesPage,
  PatientsPage,
} from '../features/portal/PortalPages'
import ManagementPage from '../features/admin/ManagementPage'
import DoctorsPage from '../features/admin/DoctorsPage'
import ReportsPage from '../features/reports/ReportsPage'
import DashboardExtras from '../features/portal/DashboardExtras'
import {
  adminRoutes,
  allRoutes,
  authRoutes,
  doctorRoutes,
  patientRoutes,
  publicRoutes,
  type AppRouteDefinition,
} from './routeConfig'

const FeaturePlaceholderPage = lazy(
  () => import('../pages/FeaturePlaceholderPage'),
)
const AuthPlaceholderPage = lazy(() => import('../pages/AuthPlaceholderPage'))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'))
const LoginPage = lazy(() => import('../features/auth/LoginPage'))
const AvailabilityPage = lazy(
  () => import('../features/availability/AvailabilityPage'),
)
const PatientAppointmentListPage = lazy(() =>
  import('../features/appointments/AppointmentPages').then((module) => ({
    default: module.PatientAppointmentListPage,
  })),
)
const BookAppointmentPage = lazy(() =>
  import('../features/appointments/AppointmentPages').then((module) => ({
    default: module.BookAppointmentPage,
  })),
)
const AppointmentDetailPage = lazy(() =>
  import('../features/appointments/AppointmentPages').then((module) => ({
    default: module.AppointmentDetailPage,
  })),
)
const DoctorAppointmentListPage = lazy(() =>
  import('../features/appointments/AppointmentPages').then((module) => ({
    default: module.DoctorAppointmentListPage,
  })),
)
const ConsultationWorkspacePage = lazy(() =>
  import('../features/consultations/ClinicalPages').then((module) => ({
    default: module.ConsultationWorkspacePage,
  })),
)
const PrescriptionListPage = lazy(() =>
  import('../features/consultations/ClinicalPages').then((module) => ({
    default: module.PrescriptionListPage,
  })),
)
const PrescriptionDetailPage = lazy(() =>
  import('../features/consultations/ClinicalPages').then((module) => ({
    default: module.PrescriptionDetailPage,
  })),
)

function RouteTitle() {
  const { pathname } = useLocation()

  useEffect(() => {
    const definition = allRoutes.find((item) => matchPath(item.path, pathname))
    document.title = `${definition?.title ?? pathname.split('/').filter(Boolean).slice(-1)[0]?.replaceAll('-', ' ') ?? 'Home'} | Online Doctor Appointment`
  }, [pathname])

  return null
}

const placeholder = (definition: AppRouteDefinition) => (
  <FeaturePlaceholderPage route={definition} />
)

const patientElement = (definition: AppRouteDefinition) => {
  if (definition.path === '/patient/payments')
    return <PaymentsPage role="patient" />
  if (definition.path === '/patient/payments/:paymentId/demo')
    return <DemoPaymentPage />
  if (definition.path === '/patient/receipts/:receiptId')
    return <ReceiptPage role="patient" />
  if (definition.path === '/patient/dashboard')
    return (
      <>
        <DashboardPage role="patient" />
        <DashboardExtras role="patient" />
      </>
    )
  if (definition.path === '/patient/files') return <FilesPage />
  if (definition.path === '/patient/profile')
    return <ProfilePage role="patient" />
  if (definition.path === '/patient/appointments')
    return <PatientAppointmentListPage />
  if (definition.path === '/patient/appointments/new')
    return <BookAppointmentPage />
  if (definition.path === '/patient/appointments/:appointmentId')
    return <AppointmentDetailPage role="patient" />
  if (definition.path === '/patient/prescriptions')
    return <PrescriptionListPage role="patient" />
  if (definition.path === '/patient/prescriptions/:prescriptionId')
    return <PrescriptionDetailPage role="patient" />
  return placeholder(definition)
}

const doctorElement = (definition: AppRouteDefinition) => {
  if (definition.path === '/doctor/dashboard')
    return (
      <>
        <DashboardPage role="doctor" />
        <DashboardExtras role="doctor" />
      </>
    )
  if (definition.path === '/doctor/calendar')
    return <DoctorAppointmentListPage calendar />
  if (definition.path === '/doctor/appointments')
    return <DoctorAppointmentListPage />
  if (definition.path === '/doctor/appointments/:appointmentId')
    return <AppointmentDetailPage role="doctor" />
  if (definition.path === '/doctor/appointments/:appointmentId/consultation')
    return <ConsultationWorkspacePage />
  if (definition.path === '/doctor/availability') return <AvailabilityPage />
  if (definition.path === '/doctor/prescriptions')
    return <PrescriptionListPage role="doctor" />
  if (definition.path === '/doctor/prescriptions/:prescriptionId')
    return <PrescriptionDetailPage role="doctor" />
  return placeholder(definition)
}

export default function AppRoutes() {
  return (
    <>
      <RouteTitle />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/doctors/:slug" element={<DoctorDetailsPage />} />
            {publicRoutes.map((definition) => (
              <Route
                key={definition.path}
                path={definition.path}
                element={<PortfolioPage />}
              />
            ))}
          </Route>

          <Route element={<AuthLayout />}>
            {authRoutes.map((definition) => (
              <Route
                key={definition.path}
                path={definition.path}
                element={
                  definition.path === '/login' ? (
                    <LoginPage />
                  ) : definition.path === '/register' ? (
                    <RegisterPage />
                  ) : (
                    <AuthPlaceholderPage route={definition} />
                  )
                }
              />
            ))}
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<RoleRoute allowedRoles={['patient']} />}>
              <Route element={<PatientLayout />}>
                <Route
                  path="/patient/change-password"
                  element={<PasswordPage role="patient" />}
                />
                <Route
                  path="/patient/notifications"
                  element={<NotificationsPage role="patient" />}
                />
                {patientRoutes.map((definition) => (
                  <Route
                    key={definition.path}
                    path={definition.path}
                    element={patientElement(definition)}
                  />
                ))}
              </Route>
            </Route>
            <Route element={<RoleRoute allowedRoles={['doctor']} />}>
              <Route element={<DoctorLayout />}>
                <Route
                  path="/doctor/reports"
                  element={<ReportsPage role="doctor" />}
                />
                <Route
                  path="/doctor/profile"
                  element={
                    <ManagementPage module="doctor-profile" role="doctor" />
                  }
                />
                <Route
                  path="/doctor/change-password"
                  element={<PasswordPage role="doctor" />}
                />
                <Route
                  path="/doctor/notifications"
                  element={<NotificationsPage role="doctor" />}
                />
                <Route
                  path="/doctor/patients"
                  element={<PatientsPage role="doctor" />}
                />
                <Route
                  path="/doctor/patients/:patientId"
                  element={<PatientsPage role="doctor" />}
                />
                {doctorRoutes.map((definition) => (
                  <Route
                    key={definition.path}
                    path={definition.path}
                    element={doctorElement(definition)}
                  />
                ))}
              </Route>
            </Route>
            <Route element={<RoleRoute allowedRoles={['admin']} />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin/doctors" element={<DoctorsPage />} />
                <Route path="/admin/doctor-profile" element={<DoctorsPage />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <>
                      <DashboardPage role="admin" />
                      <DashboardExtras role="admin" />
                    </>
                  }
                />
                <Route
                  path="/admin/appointments/new"
                  element={<BookAppointmentPage role="admin" />}
                />
                <Route
                  path="/admin/appointments/:appointmentId"
                  element={<AppointmentDetailPage role="admin" />}
                />
                <Route
                  path="/admin/prescriptions/:prescriptionId"
                  element={<PrescriptionDetailPage role="admin" />}
                />
                <Route
                  path="/admin/change-password"
                  element={<PasswordPage role="admin" />}
                />
                <Route
                  path="/admin/doctor-services"
                  element={<ManagementPage module="doctor-services" />}
                />
                <Route
                  path="/admin/reports"
                  element={<ReportsPage role="admin" />}
                />
                {adminRoutes.filter(definition=>definition.path!=='/admin/dashboard').map((definition) => (
                  <Route
                    key={definition.path}
                    path={definition.path}
                    element={
                      definition.path === '/admin/dashboard' ? (
                        <DashboardPage role="admin" />
                      ) : definition.path === '/admin/payments' ? (
                        <PaymentsPage role="admin" />
                      ) : definition.path === '/admin/receipts/:receiptId' ? (
                        <ReceiptPage role="admin" />
                      ) : (
                        <ManagementPage
                          key={definition.path}
                          module={definition.path.split('/')[2]}
                        />
                      )
                    }
                  />
                ))}
                {[
                  'patients',
                  'availability',
                  'availability-exceptions',
                  'refunds',
                  'prescriptions',
                  'pages',
                  'faqs',
                  'notification-templates',
                  'audit-logs',
                ].map((module) => (
                  <Route
                    key={module}
                    path={'/admin/' + module}
                    element={<ManagementPage key={module} module={module} />}
                  />
                ))}
                <Route
                  path="/admin/patients/:patientId"
                  element={<PatientsPage role="admin" />}
                />
                <Route
                  path="/admin/notifications"
                  element={<NotificationsPage role="admin" />}
                />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  )
}
